import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "./AppSidebar";
import { BottomNavBar } from "./BottomNavBar";
import { useAuth } from "@/hooks/use-auth";
import { DevAuthBanner } from "@/components/dev/DevAuthBanner";
import { useLanguage } from "@/hooks/use-language";
import { withAuthRoutePrefix } from "@/config/auth";

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);

  // Scroll to top on route change
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (mainRef.current) {
        mainRef.current.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    
    return () => cancelAnimationFrame(frame);
  }, [location.pathname, location.key]);

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full max-w-full">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col min-w-0 max-w-full">
          <header className="h-14 flex items-center border-b bg-background px-4 shrink-0">
            <SidebarTrigger className="mr-4 shrink-0" />
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <DevAuthBanner />
              <h1 className="text-lg font-semibold text-foreground truncate">
                {t("app.title")}
              </h1>
            </div>
            <div
              className="text-sm text-muted-foreground cursor-pointer hover:underline shrink-0"
              onClick={() => navigate(withAuthRoutePrefix('/profile', location.pathname))}
            >
              {user?.firstName}
            </div>
          </header>

          <main ref={mainRef} className="flex-1 p-4 sm:p-6 bg-background overflow-y-auto min-w-0 max-w-full" style={{ paddingBottom: 'calc(7rem + env(safe-area-inset-bottom))' }}>
            <div className="max-w-full min-w-0">
              {children}
            </div>
          </main>
          
          <BottomNavBar />
        </div>
      </div>
    </SidebarProvider>
  );
}
